import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Grid,
  Stack,
  Paper,
  TextField,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  HelpOutline as HelpIcon,
  ContentCopy as ContentCopyIcon,
  LocalOffer as LocalOfferIcon,
  Bolt as BoltIcon,
  HelpOutline
} from '@mui/icons-material';
import PaymentWidget from './PaymentWidget';

const CheckoutPage = ({
  orderData,
  onPaymentSuccess,
  onPaymentError,
  onClose,
  isOpen = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [status, setStatus] = useState({
    loaded: false,
    processing: false,
    success: false,
    error: null
  });

  // Fallback data to match the screenshot exactly


  const order = orderData?.data?.order;
  const checkoutId = orderData?.data?.checkoutId;
  const integrityId = orderData?.data?.integrityId;

  // Format currency with proper symbol


  // Handle payment success
  const handlePaymentSuccess = (response) => {
    setStatus(prev => ({ ...prev, processing: false, success: true }));
    if (onPaymentSuccess) {
      onPaymentSuccess(response);
    }
    setTimeout(() => {
      if (onClose) onClose();
    }, 3000);
  };

  // Handle payment error
  const handlePaymentError = (error) => {
    setStatus(prev => ({
      ...prev,
      processing: false,
      error: error?.message || 'Payment failed. Please try again.'
    }));
    if (onPaymentError) {
      onPaymentError(error);
    }
  };

  // Handle processing state change
  const handleProcessingChange = (isProcessing) => {
    setStatus(prev => ({ ...prev, processing: isProcessing }));
  };

  // Close handler
  const handleClose = () => {
    if (status.processing) {
      if (window.confirm('Payment is in progress. Are you sure you want to close?')) {
        if (onClose) onClose();
      }
    } else {
      if (onClose) onClose();
    }
  };

  // ============================================================
  // SUCCESS STATE
  // ============================================================
  if (status.success) {
    return (
      <Fade in timeout={500}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
            p: 3
          }}
        >
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              maxWidth: 480,
              p: { xs: 4, sm: 6 },
              borderRadius: 4,
              bgcolor: 'white',
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: '#2ecc71',
                mb: 2
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 2 }}>
              Your order has been confirmed. You will receive a confirmation email shortly.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                fontFamily: 'monospace',
                bgcolor: '#f8fafc',
                py: 1,
                px: 2,
                borderRadius: 2,
                display: 'inline-block',
                mb: 2
              }}
            >
              Order ID: {order?.referenceId || 'N/A'}
            </Typography>
            <CircularProgress size={24} sx={{ color: '#CF9D2E', display: 'block', mx: 'auto' }} />
          </Paper>
        </Box>
      </Fade>
    );
  }

  // ============================================================
  // MAIN RENDER - MATCHING THE IMAGE
  // ============================================================
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Close Button (Top Right) */}
      <IconButton
        onClick={handleClose}
        disabled={status.processing}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 20,
          color: '#64748b',
          '&:hover': { bgcolor: '#f1f5f9' }
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* ===== MAIN CONTENT ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 }, flex: 1 }}>
        {/* Error Alert */}
        {status.error && (
          <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }} onClose={() => setStatus(prev => ({ ...prev, error: null }))}>
            {status.error}
          </Alert>
        )}

        <Grid container columnSpacing={3}>
          {/* ===== LEFT - ORDER SUMMARY ===== */}
          <Grid item xs={12} md={6} lg={5}
            sx={{
              borderRight: {
                md: 1
              },
              pr: 3,
              pl: 2,
              borderColor: "borderPrimary.main",
              backgroundColor: "white.main"
            }}
          >
            <Box sx={{
              display: 'flex',
              justifyContent: "flex-start",
              alignItems: 'center',
              width: "100%",
              height: 90,
              overflow: "hidden",
              flex: 1,
            }}>
              {/* Logo Area */}
              <Box
                component={"img"}
                src={`https://www.99gift.ae/static/media/logo.1f56873eb49af1faa202.png`}
                sx={{
                  width: 150,
                  height: "auto",
                  objectFit: 'cover'
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              sx=
              {{
                fontWeight: 600,
                color: '#000',
                mb: 0.2,
                fontFamily: "Be Vietnam Pro",
                fontSize: { xs: "13px", md: "16px" },
                letterSpacing: 0.5
              }}>
              Order Summary
            </Typography>
            <Typography
              sx={{
                color: '#4B5563',
                mb: 2,
                fontFamily: "Be Vietnam Pro",
                fontSize: "10.5px",
                fontWeight: 400,
              }}>
              Review your order details
            </Typography>

            {/* Customer Info */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                mb: 2,
                p: 2,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
              }}
            >
              <Box
                sx={{
                  width: 35,
                  height: 35,
                  bgcolor: '#e2e8f0',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PersonIcon sx={{ fontSize: 20, color: '#64748B' }} />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    color: '#64748B',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    fontSize: '11px',
                    fontFamily: 'Be Vietnam Pro',
                    mb: 0.75,
                  }}
                >
                  Receiver Details
                </Typography>

                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#000',
                    fontFamily: 'Be Vietnam Pro',
                    lineHeight: 1,
                    mb: 0.5,
                  }}
                >
                  {order?.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#64748B',
                    fontFamily: 'Be Vietnam Pro',
                    lineHeight: 1.5,
                    mb: 0.3,
                    wordBreak: 'break-word',
                    fontWeight: 500,
                  }}
                >
                  {order?.email}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '10.5px',
                    color: '#64748B',
                    fontFamily: 'Be Vietnam Pro',
                    lineHeight: 1.5,
                    fontWeight: 500,
                  }}
                >
                  {order?.phoneNumber}
                </Typography>
              </Box>
            </Box>

            <Divider
              sx={{
                borderStyle: "dashed",
                color: '#64748B',
                borderWidth: "1px",
                mb: 2,
              }}
            />

            {/* Order ID Box */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 2,
                borderColor: '#e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 1
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748B',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    letterSpacing: 0.5,
                    fontFamily: 'Be Vietnam Pro',
                    fontSize: '13px',
                  }}
                >
                  Order ID :
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    fontFamily: 'Be Vietnam Pro',
                    color: '#CF9D2E',
                    fontSize: '1rem',
                    lineHeight: 1,
                  }}
                >
                  {order?.referenceId || 'N/A'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ color: '#64748B' }}
                onClick={() => navigator.clipboard.writeText(order?.referenceId || '')}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Paper>


            {/* Pricing */}
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  sx={{
                    color: '#64748B',
                    fontFamily: "Be Vietnam Pro",
                    fontSize: "11px",
                    fontWeight: 500,
                  }}>
                  Subtotal
                </Typography>
                <Typography sx={{
                  fontWeight: 500,
                  color: '#000',
                  fontSize: "10px",
                  fontFamily: "Be Vietnam Pro",
                }}>
                  AED {order?.grossAmount}
                </Typography>
              </Box>

              {order?.discountValue > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    sx={{
                      color: '#64748B',
                      fontFamily: "Be Vietnam Pro",
                      fontSize: "11px",
                      fontWeight: 500,
                    }}>
                    Discount
                  </Typography>
                  <Typography
                    sx={{
                      color: '#CF9D2E',
                      fontWeight: 600,
                      fontSize: "10px",
                      fontFamily: "Be Vietnam Pro",
                    }}>
                    AED {order?.discountValue}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Divider
              sx={{
                borderStyle: "dashed",
                color: '#64748B',
                borderWidth: "1px",
                mb: 2,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                mb: 2
              }}
            >
              <Typography sx=
                {{
                  color: '#64748B',
                  fontFamily: "Be Vietnam Pro",
                  fontSize: "12.5px",
                  fontWeight: 500,
                }}>
                Total Amount
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 700,
                  fontSize: "13px",
                  color: '#CF9D2E',
                  lineHeight: 1,
                }}>
                AED {order?.netAmount}
              </Typography>
            </Box>



            <Divider
              sx={{
                borderStyle: "dashed",
                color: '#64748B',
                borderWidth: "1px",
                mb: 2,
              }}
            />
          </Grid>

          {/* ===== RIGHT - PAYMENT ===== */}
          <Grid item xs={12} md={6} lg={7}
            sx={{
              py: 2,
            }}
          >
            <Box>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ bgcolor: 'white.light', borderRadius: '50%', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LockIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontFamily: "Be Vietnam Pro"
                      }}
                    >
                      Secure Payment
                    </Typography>
                    <Typography variant="body2" sx={{
                      fontWeight: 500,
                      fontFamily: "Be Vietnam Pro",
                      color: '#5f5f5f'
                    }}>
                      All transactions are 100% secure and encrypted
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Secure Connection"
                  size="small"
                  sx={{
                    bgcolor: '#e8f5e9',
                    color: 'green.main',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    height: 28,
                    fontFamily: "Be Vietnam Pro",
                    borderRadius: 2
                  }}
                />
              </Box>

              {/* Payment Widget or Processing State */}
              <Box
                sx={{
                  minHeight: "100%",
                  width: "100%",
                  border: '1px solid #00286514',
                  borderRadius: 2,
                  p: 2,
                  overflow: "auto",
                }}
              >
                {status.processing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#CF9D2E' }} />
                    <Typography variant="body1" sx={{ fontFamily: "Be Vietnam Pro", fontWeight: 600, color: '#1a1a2e' }}>
                      Processing Payment...
                    </Typography>
                  </Box>
                ) : (
                  <PaymentWidget
                    orderData={orderData}
                    onPaymentSuccess={handlePaymentSuccess}
                    onPaymentError={handlePaymentError}
                    onProcessingChange={handleProcessingChange}
                  />
                )}
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <LockIcon sx={{ fontSize: 16, color: '#94a3b8', verticalAlign: 'middle', mr: 0.5 }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#898e95ff',
                    fontFamily: "Be Vietnam Pro",
                    fontSize: "10px",
                    fontWeight: 500,
                  }}
                >
                  By paying, you agree to our{' '}
                  <Typography
                    component="a"
                    href="https://www.99gift.ae/termsandcondition"
                    target='_blank'
                    sx={{
                      color: '#000000',
                      textDecoration: 'underline',
                      fontWeight: 600,
                      fontFamily: "Be Vietnam Pro",
                      cursor: "pointer",
                    }}
                  >
                    Terms of Service
                  </Typography>
                  {' '}and{' '}
                  <Typography
                    component="a"
                    href="https://www.99gift.ae/privacypolicy"
                    target='_blank'
                    sx={{
                      color: '#000000',
                      textDecoration: 'underline',
                      fontWeight: 600,
                      fontFamily: "Be Vietnam Pro",
                      cursor: "pointer",
                    }}>
                    Privacy Policy
                  </Typography>
                  .
                </Typography>
              </Box>

              {/* Footer Badges */}
              <Stack spacing={1.5}
                sx={{
                  mt: 3,
                  display: 'flex',
                  flexDirection: "row",
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShieldIcon sx={{ fontSize: 24, color: '#2ecc71' }} />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: '#000',
                        fontSize: '0.8rem',
                        fontFamily: "Be Vietnam Pro",
                      }}>
                      SSL Encrypted
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
                      256-bit secure connection
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SecurityIcon sx={{ fontSize: 24, color: '#3498db' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.8rem', fontFamily: "Be Vietnam Pro", }}>
                      PCI DSS Compliant
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
                      Your data is safe with us
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BoltIcon sx={{ fontSize: 24, color: '#f1c40f' }} />
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#1a1a2e', fontSize: '0.8rem', fontFamily: "Be Vietnam Pro", }}>
                      Instant Processing
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: "Be Vietnam Pro", fontWeight: 600 }}>
                      Quick and secure payments
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CheckoutPage;
